import { onAuthStateChanged } from '@firebase/auth'
import { useRouter } from 'next/router'
import { useEffect, useRef } from 'react'
import ReactGA from 'react-ga4'

import { auth } from '@/services/firebase.service'

const GoogleAnalytics = () => {
  const router = useRouter()

  const isAuthenticatedRef = useRef<boolean | null>(null)
  const authReadRef = useRef<boolean>(false)
  const initialPageviewSentRef = useRef<boolean>(false)

  const sendPageview = (url: string) => {
    if (process.env.NODE_ENV !== 'production') return
    const isAuthenticated = !!isAuthenticatedRef.current
    ReactGA.send({
      authentication: isAuthenticated ? 'authenticated' : 'unauthenticated',
      hitType: 'pageview',
      page: url,
    })
  }

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return

    ReactGA.initialize(process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID as string, {
      gaOptions: {
        send_page_view: false, // Wait to send initial pageview until we've loaded auth
      },
    })

    const unsub = onAuthStateChanged(auth, (user) => {
      isAuthenticatedRef.current = !!user
      authReadRef.current = true

      // Send the initial pageview once auth state is known
      if (!initialPageviewSentRef.current) {
        sendPageview(router.asPath)
        initialPageviewSentRef.current = true
      }
    })
    return () => unsub()
  }, [])

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return

    const handleRouteChange = (url: string) => {
      if (!authReadRef.current) return
      sendPageview(url)
    }

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => router.events.off('routeChangeComplete', handleRouteChange)
  }, [router.events])

  return null
}

export default GoogleAnalytics
